import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { SeoMetaPort } from './seo-meta.port';
import { LinkService } from '@app/core/services/link-service';
import { PublicSeoHreflangLink } from '.';
import { OgPayload } from './resources/seo/og-payload';
import { PageSeoPayload } from './resources/seo/page-seo-payload';
import { assetUrl } from '@ui/shared/helpers/asset-url.helper';

/**
 * Angular adapter: implements SeoMetaPort using existing LinkService and Angular Meta.
 *
 * Key fix (REQ-012): clearHreflangs() removes ALL <link rel="alternate"> before adding new ones,
 * fixing the hreflang accumulation bug.
 *
 * OG tags: uses Meta.updateTag() (idempotent).
 * - og:* and fb:pages tags use { property: '...' } selector (Open Graph convention).
 * - twitter:* tags use { name: '...' } selector (Twitter convention).
 */
@Injectable()
export class AngularSeoMetaAdapter extends SeoMetaPort {
    private static readonly MANAGED_PRELOAD_SELECTOR: string =
        'link[rel="preload"][data-managed-preload="true"]';
    private static readonly MANAGED_JSONLD_SELECTOR: string =
        'script[type="application/ld+json"][data-managed-jsonld="true"]';
    private readonly document: Document = inject(DOCUMENT);
    private readonly meta: Meta = inject(Meta);
    private readonly title: Title = inject(Title);
    private readonly linkService: LinkService = inject(LinkService);
    private readonly platformId: object = inject(PLATFORM_ID);

    /** REQ-012: remove ALL existing alternate link tags using LinkService (SSR-safe via Renderer2) */
    public override clearHreflangs(): void {
        this.linkService.removeTag('link[rel="alternate"]');
    }

    /** REQ-013: clear existing, then add new hreflang entries */
    public override setHreflangs(links: ReadonlyArray<PublicSeoHreflangLink>): void {
        this.clearHreflangs();

        links.forEach((entry: PublicSeoHreflangLink): void => {
            this.linkService.addHreflangTag({
                rel: 'alternate',
                href: entry.href,
                hreflang: entry.hreflang,
            });
        });
    }

    /** REQ-014: delegate to LinkService (Renderer2, SSR-safe) */
    public override setCanonical(url: string): void {
        this.linkService.addTag({
            rel: 'canonical',
            href: url,
        });
    }

    /** REQ-015: use Angular Meta.updateTag for robots meta */
    public override setRobots(content: string): void {
        this.meta.updateTag({ name: 'robots', content });
    }

    public override setHtmlLang(lang: string): void {
        this.document.documentElement.lang = lang;
    }

    public override setPageSeo(payload: PageSeoPayload): void {
        this.title.setTitle(payload.title);
        this.meta.updateTag({ name: 'description', content: payload.description });
        this.meta.updateTag({ name: 'keywords', content: payload.keywords });
    }

    /**
     * Set OG + Twitter Card meta tags synchronously, before first render.
     * og:* and fb:pages → { property } attribute (Open Graph spec).
     * twitter:* → { name } attribute (Twitter spec).
     */
    public override setOpenGraph(payload: OgPayload): void {
        // Open Graph tags (property-based)
        this.meta.updateTag({ property: 'og:title', content: payload.title });
        this.meta.updateTag({ property: 'og:description', content: payload.description });
        this.meta.updateTag({ property: 'og:url', content: payload.url });
        this.meta.updateTag({ property: 'og:image', content: payload.image });
        this.meta.updateTag({ property: 'og:site_name', content: payload.siteName });
        if (payload.locale) {
            this.meta.updateTag({ property: 'og:locale', content: payload.locale });
        }

        // Twitter Card tags (name-based)
        if (payload.twitterCard) {
            this.meta.updateTag({ name: 'twitter:card', content: payload.twitterCard });
        }
        if (payload.twitterSite) {
            this.meta.updateTag({ name: 'twitter:site', content: payload.twitterSite });
        }
        this.meta.updateTag({ name: 'twitter:title', content: payload.title });
        this.meta.updateTag({ name: 'twitter:description', content: payload.description });
        this.meta.updateTag({ name: 'twitter:image', content: payload.image });

        // Facebook pages (property-based per OG spec)
        if (payload.fbPages) {
            this.meta.updateTag({ property: 'fb:pages', content: payload.fbPages });
        }
    }

    public override setJsonLd(json: string | null): void {
        this.document.head
            .querySelectorAll(AngularSeoMetaAdapter.MANAGED_JSONLD_SELECTOR)
            .forEach((element: Element): void => element.remove());

        if (json === null) {
            return;
        }

        const script: HTMLScriptElement = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-managed-jsonld', 'true');
        script.text = json;
        this.document.head.appendChild(script);
    }

    /**
     * Font preloads only make sense while the document is being built on the server: the browser
     * discovers them during HTML parsing, before the stylesheet triggers the @font-face fetch.
     *
     * Re-running this on the client (every NavigationEnd, hydration included) would remove the
     * server-rendered tags and append brand-new ones for fonts that are already downloaded. Those
     * fresh declarations have no consumer left, which is exactly what makes Chrome log
     * "preloaded using link preload but not used".
     */
    public override setPreloads(hrefs: ReadonlyArray<string>): void {
        if (!isPlatformServer(this.platformId)) {
            return;
        }

        this.linkService.removeTag(AngularSeoMetaAdapter.MANAGED_PRELOAD_SELECTOR);

        hrefs.forEach((href: string): void => {
            const preloadTag: Record<string, string> = {
                rel: 'preload',
                href: assetUrl(href),
                as: 'font',
                type: 'font/woff2',
                crossorigin: 'anonymous',
                'data-managed-preload': 'true',
            };

            this.linkService.addHreflangTag(preloadTag);
        });
    }
}
